import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Countdown from "@/components/Countdown";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import TrialSlotModal from "@/components/TrialSlotModal";
import { api, fileUrl } from "@/lib/api";
import { Trophy, MapPin, Calendar, Users, Zap, ShieldCheck, ChevronDown, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HERO_BG = "https://images.pexels.com/photos/32945758/pexels-photo-32945758.jpeg";
const GALLERY_STOCK = [
  "https://images.pexels.com/photos/29463867/pexels-photo-29463867.jpeg",
  "https://images.unsplash.com/photo-1565787113569-be8aa6f5da41",
  "https://images.unsplash.com/photo-1637635753380-20bf6f46ede0",
  "https://images.unsplash.com/photo-1531415074968-036ba1b575da",
  "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e",
  "https://images.pexels.com/photos/3628912/pexels-photo-3628912.jpeg",
];

export default function Home() {
  const [settings, setSettings] = useState({});
  const [sponsors, setSponsors] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const [trialOpen, setTrialOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const galleryToShow = gallery.length ? gallery.map((g) => ({ url: fileUrl(g.file_id), caption: g.caption })) : GALLERY_STOCK.map((u) => ({ url: u, caption: "NDPL" }));

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden">
        <img src={HERO_BG} alt="Stadium" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        <div className="absolute inset-0 stadium-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#39FF14]/40 bg-[#39FF14]/5 text-[#39FF14] text-xs uppercase tracking-[0.3em] mb-8">
            <span className="w-2 h-2 rounded-full bg-[#39FF14] pulse-neon" />
            Season 2026 • Registration Open
          </div>
          <h1 data-testid="hero-title" className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem] leading-[0.9] text-white text-shadow-hero">
            {(() => {
              const t = settings.home_hero_title || "Marwar's Biggest T10 Cricket League";
              const words = t.split(" ");
              const head = words.slice(0, -2).join(" ");
              const mid = words.slice(-2, -1)[0] || "";
              const last = words.slice(-1)[0] || "";
              return (<>
                <span className="block">{head}</span>
                <span className="block"><span className="text-[#39FF14]">{mid}</span> <span className="text-[#FFD700]">{last}</span></span>
              </>);
            })()}
          </h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-white/80">
            {settings.home_hero_subtitle || "Register • Trial • Get Auctioned • Play"}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <button data-testid="cta-register" onClick={() => nav("/register")} className="px-8 py-4 bg-[#39FF14] text-black font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(57,255,20,0.7)] transition-all">Register Now — ₹{settings.registration_fee || 799}</button>
            <button data-testid="cta-player-login" onClick={() => nav("/player/login")} className="px-8 py-4 border-2 border-[#FFD700] text-[#FFD700] font-bold uppercase tracking-widest hover:bg-[#FFD700] hover:text-black transition-all">Player Login</button>
          </div>

          <div className="mt-10 p-6 md:p-8 bg-gradient-to-r from-[#0B132B] to-[#151F3D] border border-[#FFD700]/40 rounded-lg flex flex-col md:flex-row items-center justify-between gap-4" data-testid="selected-players-cta">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#FFD700] grid place-items-center text-black text-xl">★</div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFD700]">Official Squad</div>
                <div className="font-display text-2xl md:text-3xl">Selected <span className="text-[#39FF14]">Players</span></div>
                <div className="mt-1 text-xs text-white/70">Browse officially selected players from all 17 tehsils.</div>
              </div>
            </div>
            <a href="/selected-players" data-testid="selected-players-cta-btn" className="px-6 py-3 bg-[#FFD700] text-black font-bold uppercase tracking-widest text-sm hover:bg-white transition-colors">View Selected Players</a>
          </div>

          <div className="mt-6 p-6 md:p-8 bg-gradient-to-r from-[#0B132B] to-[#151F3D] border border-[#39FF14]/40 rounded-lg flex flex-col md:flex-row items-center justify-between gap-4 glow-green" data-testid="book-trial-slot">
            <div className="flex items-center gap-4">
              <Calendar className="text-[#39FF14]" size={30} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">Book Trial Slot</div>
                <div className="font-display text-2xl md:text-3xl">20 September 2026</div>
                <div className="mt-1 text-xs text-white/70">Makrana</div>
              </div>
            </div>
            <button onClick={() => setTrialOpen(true)} data-testid="book-trial-slot-btn" className="px-6 py-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-sm hover:bg-white transition-colors">Book Trial Slot</button>
          </div>

          <div className="mt-14">
            <div className="text-xs uppercase tracking-[0.4em] text-white/60 mb-4">Kick-off in</div>
            <Countdown target={settings.countdown_target} />
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 animate-bounce"><ChevronDown /></div>
        </div>
      </section>

      {/* ABOUT + VISION */}
      <section id="about" className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">About NDPL</div>
            <h2 className="font-display text-5xl md:text-6xl mt-3">Where Marwar's <span className="text-[#FFD700]">Cricket Legacy</span> Begins</h2>
            <p className="mt-6 text-white/70 leading-relaxed">{settings.home_about}</p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[{ i: MapPin, v: "17", l: "Tehsils" }, { i: Users, v: "1000+", l: "Players" }, { i: Trophy, v: "20", l: "Franchises" }].map((s) => (
                <div key={s.l} className="p-5 bg-[#0B132B] border border-white/10">
                  <s.i className="text-[#39FF14] mb-2" size={20} />
                  <div className="font-display text-3xl">{s.v}</div>
                  <div className="text-xs uppercase tracking-widest text-white/60">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 border border-[#39FF14]/20 rounded-lg" />
            <div className="relative p-8 md:p-10 bg-gradient-to-br from-[#0B132B] to-[#151F3D] border border-white/10 rounded-lg noise">
              <Zap className="text-[#FFD700] mb-4" />
              <div className="text-xs uppercase tracking-[0.4em] text-[#FFD700]">League Vision</div>
              <h3 className="font-display text-4xl mt-3">Talent knows no <span className="text-[#39FF14]">boundary</span></h3>
              <p className="mt-5 text-white/70 leading-relaxed">{settings.home_vision}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRIZE POOL */}
      <section id="prize" className="py-24 md:py-32 relative overflow-hidden border-t border-white/5">
        <div className="stadium-grid absolute inset-0 opacity-30" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Total Prize Pool</div>
          <div className="mt-6 inline-block relative">
            <div className="absolute -inset-8 bg-[#39FF14]/10 blur-3xl" />
            <div className="relative font-display text-7xl sm:text-8xl md:text-[10rem] leading-none text-[#FFD700]" data-testid="prize-pool">{settings.prize_pool || "₹10 Lakh+"}</div>
          </div>
          <p className="mt-6 text-white/70 max-w-2xl mx-auto">A prize pool worthy of a premier league. Winners, runners-up, MVPs, orange & purple caps — every performance rewarded.</p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { l: "Winner", a: "₹7,00,000" },
              { l: "Runner Up", a: "₹4,00,000" },
              { l: "3rd Place", a: "₹1,00,000" },
              { l: "4th Place", a: "₹1,00,000" },
            ].map((x) => (
              <div key={x.l} className="p-6 bg-[#0B132B] border border-[#FFD700]/20">
                <Trophy className="text-[#FFD700] mx-auto mb-3" />
                <div className="text-sm uppercase tracking-widest">{x.l}</div>
                <div className="mt-2 font-display text-2xl text-[#39FF14]">{x.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* TRIAL BANNER */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 p-8 md:p-12 bg-gradient-to-r from-[#0B132B] to-[#151F3D] border border-[#39FF14]/30 flex flex-col md:flex-row items-center justify-between gap-6 rounded-lg glow-green">
          <div className="flex items-center gap-4">
            <Calendar className="text-[#39FF14]" size={36} />
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#39FF14]">Book Trial Slot</div>
              <div className="font-display text-3xl md:text-4xl">20 September 2026 • Makrana</div>
            </div>
          </div>
          <button onClick={() => setTrialOpen(true)} data-testid="trial-cta" className="px-8 py-4 bg-[#39FF14] text-black font-bold uppercase tracking-widest hover:bg-white">Book Trial Slot</button>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Gallery</div>
              <h2 className="font-display text-5xl mt-3">Moments from the Turf</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {galleryToShow.map((g, i) => (
              <div key={g.url || `g-${i}`} className={`relative overflow-hidden border border-white/10 ${i === 0 ? "md:col-span-2 md:row-span-2" : ""}`}>
                <img src={g.url} alt={g.caption} className="w-full h-full object-cover aspect-[4/3] hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-4">
                  <div className="text-xs uppercase tracking-widest">{g.caption}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPONSORS */}
      <section id="sponsors" className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Powered By</div>
            <h2 className="font-display text-5xl mt-3">Our Sponsors</h2>
          </div>
          {sponsors.length === 0 ? (
            <div className="max-w-3xl mx-auto p-10 border border-dashed border-[#FFD700]/40 bg-[#0B132B] text-center" data-testid="sponsors-empty">
              <div className="font-display text-4xl md:text-5xl text-[#FFD700]">Sponsors Coming Soon</div>
              <p className="mt-3 text-white/60 text-sm">Founding partner slots are still open. Reach out to us at <span className="text-[#39FF14]">{settings.contact_email || "contact@ndpl.in"}</span></p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {sponsors.map((s) => (
                <a key={s.id} href={s.website || "#"} target="_blank" rel="noreferrer" className="p-6 bg-[#0B132B] border border-white/10 hover:border-[#FFD700] transition-colors grid place-items-center">
                  {s.logo_file_id ? <img src={fileUrl(s.logo_file_id)} alt={s.name} className="max-h-16 object-contain" /> : <div className="font-display text-xl">{s.name}</div>}
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section id="faqs" className="py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">FAQ</div>
            <h2 className="font-display text-5xl mt-3">Answers, before you ask</h2>
          </div>
          <div className="space-y-3">
            {(settings.faqs || []).map((f, i) => (
              <div key={f.q || `faq-${i}`} className="border border-white/10 bg-[#0B132B]" data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-medium">{f.q}</span>
                  <ChevronDown className={`transition-transform ${openFaq === i ? "rotate-180 text-[#39FF14]" : "text-white/50"}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-white/70 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HPL SELECTED PLAYERS */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <a href="/hpl-players" className="block p-6 md:p-8 bg-gradient-to-r from-[#151F3D] to-[#0B132B] border border-[#39FF14]/40 rounded-lg hover:border-[#39FF14] transition-colors" data-testid="hpl-cta">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#39FF14] grid place-items-center text-black text-xl">🏏</div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">Harsore Premier League</div>
                  <div className="font-display text-2xl md:text-3xl">HPL <span className="text-[#FFD700]">(Harsore Premier League) Selected Players</span></div>
                  <div className="mt-1 text-xs text-white/70">Tap to view all 43 HPL selected players and register for the auction.</div>
                </div>
              </div>
              <span className="px-6 py-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-sm">View HPL Players</span>
            </div>
          </a>
        </div>
      </section>

      <Footer settings={settings} />
      <FloatingWhatsapp />
      <TrialSlotModal open={trialOpen} onClose={() => setTrialOpen(false)} />
    </div>
  );
}
